import { useEffect, useState } from 'react';
import { endpoints } from '../../api';
import { faceCatalog, type AvatarCatalog } from '../../api/avatar-catalog';
import { leaseArtwork, type ArtworkLease } from './studio-artwork-cache';

const REQUESTED = 24;

/** Ключ объекта не является URL. Изображения читаются через авторизованный BFF, без токена в src.
    blob-URL общий на сессию (studio-artwork-cache): форма, открытая после витрины, получает их сразу.
    pending — картинки ещё едут (список или портреты): витрина держит под них место и не перекладывается, когда они придут. */
export function useStudioArtwork(locale: 'ru' | 'en', attempt = 0) {
  const [state, setState] = useState<{ locale: string; data?: AvatarCatalog; error: boolean; pending: boolean }>({
    locale: '',
    error: false,
    pending: true,
  });
  useEffect(() => {
    const controller = new AbortController();
    const leases: ArtworkLease[] = [];
    let disposed = false;
    const live = () => !disposed && !controller.signal.aborted;
    void (async () => {
      const epoch = endpoints.sessionEpoch?.() ?? 0;
      // Владелец и список — параллельно: на стенде /app/me отвечает ~2 с, каталог ~0,7 с.
      const [, full] = await Promise.all([endpoints.me(), endpoints.avatarCatalog.list(locale, controller.signal)]);
      // Ряды лиц и роли — без реальных людей каталога (D-270), они только во вкладке «Актёры» «Озвучки».
      const catalog = faceCatalog(full);
      // Список нужен рядам «Кто в кадре» сразу; до прихода картинок карточки — с буквой.
      if (live()) setState({ locale, data: catalog, error: false, pending: true });
      // Рекомендованные идут первыми: их показывают витрина и первый ряд формы.
      const ordered = [...new Set([...catalog.recommended_ids, ...catalog.avatars.slice(0, 12).map(card => card.id)])].slice(0, REQUESTED);
      const rank = new Map(ordered.map((id, index) => [id, index]));
      const cards = catalog.avatars.filter(card => rank.has(card.id) && card.image_url.startsWith('live-avatar/'));
      const pending = cards.map(card => {
        const lease = leaseArtwork(epoch, `${card.id}:${card.version}`, () => endpoints.avatarCatalog.image(card.id), rank.get(card.id));
        leases.push(lease);
        return lease.url.then(url => [card.id, url] as const);
      });
      const loaded = await Promise.allSettled(pending);
      // Сессия сменилась (выход, слияние аккаунтов) — картинки прежнего владельца не показываем.
      if ((endpoints.sessionEpoch?.() ?? 0) !== epoch) throw new Error('owner_changed');
      const images = new Map(loaded.flatMap(result => (result.status === 'fulfilled' ? [result.value] : [])));
      if (live())
        setState({
          locale,
          data: { ...catalog, avatars: catalog.avatars.map(card => ({ ...card, image_url: images.get(card.id) ?? card.image_url })) },
          error: loaded.some(result => result.status === 'rejected'),
          pending: false,
        });
    })().catch(() => {
      if (!disposed) setState({ locale, error: true, pending: false });
    });
    return () => {
      disposed = true;
      controller.abort();
      leases.forEach(lease => lease.release());
    };
  }, [locale, attempt]);
  return state.locale === locale ? state : { locale, error: false, pending: true };
}
